import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ApiService } from "../services/api";
import "./create_image.scss";
import { useNavigate } from "react-router-dom";

const CreateImage = () => {
  const navigate = useNavigate();
  // USER & UI STATE
  const [availableCredits, setAvailableCredits] = useState(0);
  const [userId, setUserId] = useState(""); // General ID (Phone or Email)
  const [prompt, setPrompt] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImageUrl, setGeneratedImageUrl] = useState(null);
  const [acceptedResponsibility, setAcceptedResponsibility] = useState(false);

  // CONFIGURATION STATE
  const [selectedRatio, setSelectedRatio] = useState("1:1");
  const requiredCredits = 1;

  useEffect(() => {
    fetchUserInfo();
  }, []);

  const fetchUserInfo = async () => {
    try {
      // 1. IDENTITY FALLBACK: Check phone first, then email
      const phoneNumber = localStorage.getItem("phoneNumber");
      const email = localStorage.getItem("userEmail");
      const activeId = phoneNumber || email || "";

      setUserId(activeId);

      if (!activeId) {
        console.error("No user identity found. Redirecting to login...");
        return;
      }

      // 2. FETCH CREDITS using the active identity
      const response = await ApiService.getUserInfo(activeId);
      const data = response.data || response;
      if (data) {
        setAvailableCredits(Number(data.availableCredits) || 0);
      }
    } catch (e) {
      console.error("Error fetching user info:", e);
    }
  };

  // Check balance using Numbers to avoid string comparison bugs
  const hasLowBalance = Number(availableCredits) < Number(requiredCredits);

  const handleGenerate = async () => {
    if (hasLowBalance) {
      navigate("/pricing");
      return;
    }
    if (!acceptedResponsibility) return;
    if (!prompt.trim() || isGenerating) return;

    setIsGenerating(true);
    setGeneratedImageUrl(null);

    try {
      const data = await ApiService.generateImage({
        prompt: prompt,
        userId: userId,
        aspect_ratio: selectedRatio,
      });

      if (data && data.imageUrl) {
        setGeneratedImageUrl("https://api.zulluai.com" + data.imageUrl);
        const deductResponse = await ApiService.deductCreditsForIdentifier(
          userId,
          requiredCredits
        );

        if (!(deductResponse.success || deductResponse.status === "success")) {
          throw new Error(deductResponse.msg || "Credit deduction failed");
        }

        setAvailableCredits((prev) => Number(prev) - requiredCredits);
        window.dispatchEvent(new Event("zulluCreditsUpdated"));
      } else {
        throw new Error(
          "Zullu AI failed to return an image. Please try a different prompt."
        );
      }
    } catch (e) {
      console.error("Process failed:", e);
      alert(e.message || "Error processing request");
    } finally {
      setIsGenerating(false);
    }
  };

  const downloadImage = async () => {
    try {
      const response = await fetch(generatedImageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `ZulluAI_${Date.now()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      window.open(generatedImageUrl, "_blank"); // Fallback
    }
  };

  return (
    <div className="zullu-image-container">
      <div className="main-display">
        <AnimatePresence mode="wait">
          {!generatedImageUrl && !isGenerating ? (
            <motion.div
              key="hero"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="hero-text"
            >
              <h1>Create AI Image</h1>
              <p>Visionary visuals powered by Zullu AI Imagine.</p>
            </motion.div>
          ) : isGenerating ? (
            <motion.div key="loading" className="loading-state">
              <div className="main-spinner"></div>
              <p>Zullu AI is imagining your prompt...</p>
            </motion.div>
          ) : (
            <motion.div
              key="result"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="result-viewer"
            >
              <div className="image-card">
                <img src={generatedImageUrl} alt="AI Generated" />
                <button
                  className="download-floating-btn"
                  onClick={downloadImage}
                >
                  <i className="fi fi-rr-download"></i>
                </button>
              </div>
              <button
                className="clear-btn"
                onClick={() => setGeneratedImageUrl(null)}
              >
                Generate New Image
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="fixed-bottom-bar">
        <AnimatePresence>
          {hasLowBalance && userId && (
            <motion.div
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="warning-toast"
            >
              Insufficient credits. 1 credit required.
            </motion.div>
          )}
        </AnimatePresence>

        <div className="command-card">
          <div className="input-area">
            <textarea
              placeholder="Describe what you want to see..."
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              disabled={isGenerating}
            />
          </div>

          <label className="image-responsibility-check">
            <input
              type="checkbox"
              checked={acceptedResponsibility}
              onChange={(event) => setAcceptedResponsibility(event.target.checked)}
            />
            <span>
              I accept the{" "}
              <button type="button" onClick={() => navigate("/user-responsibility")}>
                User Responsibility
              </button>
            </span>
          </label>

          <div className="toolbar-area">
            <div className="config-group">
              <div className="pill-select">
                <i className="fi fi-rr-expand"></i>
                <select
                  value={selectedRatio}
                  onChange={(e) => setSelectedRatio(e.target.value)}
                >
                  <option value="1:1">1:1 Square</option>
                  <option value="16:9">16:9 Cinema</option>
                  <option value="9:16">9:16 Portrait</option>
                  <option value="4:3">4:3 Photo</option>
                </select>
              </div>
            </div>

            <div className="action-group">
              <span className={`credits-tag ${hasLowBalance ? "low" : ""}`}>
                1 Credit required
              </span>
              <button
                className={`send-btn ${
                  hasLowBalance || !acceptedResponsibility ? "disabled" : ""
                }`}
                onClick={handleGenerate}
                disabled={isGenerating || !acceptedResponsibility}
              >
                {isGenerating ? (
                  <div className="spinner-small"></div>
                ) : (
                  <i className="fi fi-rr-paper-plane"></i>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateImage;
